import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useNavigate, useParams } from 'react-router-dom';
import { TableLink } from '@/components/global';
import { LocationLink } from '@/components/locations';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ContentPageHeader, MetricGrid, SummaryMetricCard } from '@/components/global';
import { WarehouseAssortmentPanel, WarehouseTypeBadge } from '@/components/locations';
import { DaysOfCoverBadge, StockStatusBadge } from '@/components/inventory';
import { ProductIdentity } from '@/components/products';
import { Monitor, Timer, PoundSterling, Calendar, Pencil, Truck } from 'lucide-react';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
  parentWarehouseId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postcode: string | null;
  country: string;
  targetStockDays: number;
  monthlyOperatingCost: number;
  isActive: boolean;
  parentWarehouse?: Warehouse;
}

interface StockLevel {
  id: string;
  productId: string;
  quantityOnHand: number;
  quantityReserved: number;
  minLevel: number | null;
  maxLevel: number | null;
  daysOfCover: number;
  ragStatus: 'green' | 'amber' | 'red';
  product?: {
    id: string;
    sku: string;
    name: string;
    imageUrl?: string | null;
    media?: Array<{ url: string; mediaType: string }>;
  };
}

interface Transfer {
  id: string;
  transferNumber: string;
  transferDate: string;
  status: string;
  totalQuantity: number;
  fromWarehouse?: {
    name: string;
  };
}

export default function SpokeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [warehouseRes, stockRes, transfersRes] = await Promise.all([
        apiFetch(`/api/v1/warehouses/${id}`),
        apiFetch(`/api/v1/warehouses/${id}/stock`),
        apiFetch(`/api/v1/transfers?toWarehouseId=${id}`),
      ]);

      if (warehouseRes.ok) {
        const data = await warehouseRes.json();
        setWarehouse(data);
      }

      if (stockRes.ok) {
        const data = await stockRes.json();
        setStockLevels(data);
      }

      if (transfersRes.ok) {
        const data = await transfersRes.json();
        setTransfers(data.items || []);
      }
    } catch (error) {
      console.error('Error loading spoke data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="space-y-6">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="space-y-6">
          <div className="text-center py-8 text-muted-foreground">Spoke not found</div>
        </div>
      </div>
    );
  }

  const totalStockValue = stockLevels.reduce((sum, sl) => sum + sl.quantityOnHand, 0);
  const avgDaysOfCover =
    stockLevels.length > 0
      ? Math.round(
          stockLevels.reduce((sum, sl) => sum + (sl.daysOfCover === Infinity ? 99 : sl.daysOfCover), 0) /
            stockLevels.length
        )
      : 0;

  const lastDelivery = transfers.filter((t) => t.status === 'RECEIVED').sort(
    (a, b) => new Date(b.transferDate).getTime() - new Date(a.transferDate).getTime()
  )[0];

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <ContentPageHeader
          back={{ onClick: () => navigate('/locations') }}
          title={warehouse.name}
          badge={<WarehouseTypeBadge type="SPOKE" />}
          description={
            <>
              {[warehouse.city, warehouse.postcode].filter(Boolean).join(', ')}
              {warehouse.parentWarehouse ? (
                <span className="ml-2">
                  • Hub:{' '}
                  <LocationLink
                    locationType="HUB"
                    locationId={warehouse.parentWarehouse.id}
                    name={warehouse.parentWarehouse.name}
                  />
                </span>
              ) : null}
            </>
          }
          actions={
            <>
              <Button
                onClick={() => navigate(`/locations/transfers/new?toWarehouseId=${id}`)}
                className="w-full sm:w-auto"
              >
                <Truck className="mr-2 h-4 w-4" />
                Plan Delivery
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/locations/spokes/${id}/edit`)}
                className="w-full sm:w-auto"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </>
          }
        />

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Machines"
            value={0}
            icon={Monitor}
          />
          <SummaryMetricCard
            label="Avg Days Cover"
            value={`${avgDaysOfCover} days`}
            icon={Timer}
            status={avgDaysOfCover < 7 ? 'red' : avgDaysOfCover < 14 ? 'amber' : 'green'}
          />
          <SummaryMetricCard
            label="Stock Value"
            value={`${totalStockValue.toLocaleString()} units`}
            icon={PoundSterling}
          />
          <SummaryMetricCard
            label="Last Delivery"
            value={lastDelivery ? new Date(lastDelivery.transferDate).toLocaleDateString() : 'Never'}
            icon={Calendar}
          />
        </MetricGrid>

        <Tabs defaultValue="stock" className="space-y-4">
          <TabsList>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="stock">Stock Levels</TabsTrigger>
            <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            {id ? <WarehouseAssortmentPanel warehouseId={id} /> : null}
          </TabsContent>

          <TabsContent value="stock">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">On Hand</TableHead>
                    <TableHead className="hidden text-right md:table-cell">Reserved</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Min</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Max</TableHead>
                    <TableHead className="hidden sm:table-cell">Days Cover</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockLevels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No stock in this spoke
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockLevels.map((sl) => (
                      <TableRow key={sl.id}>
                        <TableCell>
                          <ProductIdentity
                            productId={sl.productId}
                            name={sl.product?.name ?? '—'}
                            sku={sl.product?.sku}
                            showSku={false}
                            size="sm"
                            product={{
                              imageUrl: sl.product?.imageUrl ?? null,
                              media: sl.product?.media ?? undefined,
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{sl.product?.sku || '-'}</TableCell>
                        <TableCell className="text-right">{sl.quantityOnHand}</TableCell>
                        <TableCell className="hidden text-right md:table-cell">{sl.quantityReserved}</TableCell>
                        <TableCell className="hidden text-right lg:table-cell">{sl.minLevel ?? '-'}</TableCell>
                        <TableCell className="hidden text-right lg:table-cell">{sl.maxLevel ?? '-'}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <DaysOfCoverBadge days={sl.daysOfCover} />
                        </TableCell>
                        <TableCell>
                          <StockStatusBadge
                            quantityOnHand={sl.quantityOnHand}
                            minLevel={sl.minLevel}
                            maxLevel={sl.maxLevel}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="deliveries">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transfer #</TableHead>
                    <TableHead className="hidden sm:table-cell">Date</TableHead>
                    <TableHead>From Hub</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Quantity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No deliveries to this spoke
                      </TableCell>
                    </TableRow>
                  ) : (
                    transfers.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>
                          <TableLink to={`/locations/transfers/${t.id}`}>
                            {t.transferNumber}
                          </TableLink>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{new Date(t.transferDate).toLocaleDateString()}</TableCell>
                        <TableCell>{t.fromWarehouse?.name || '-'}</TableCell>
                        <TableCell className="hidden text-right sm:table-cell">{t.totalQuantity}</TableCell>
                        <TableCell>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-muted">
                            {t.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
